import { useEffect } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import { SITE_ROUTES } from "@/config/siteRoutes";
import { useServicesArchive } from "@/data/servicesArchive";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";

import styles from "./ServicesPage.module.scss";

export function ServicesPage() {
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const data = useServicesArchive();
  const content = data.presentation;

  useEffect(() => {
    document.title = `${data.seoTitle || data.title} | VenueX`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", data.searchDescription || data.intro);
  }, [data.intro, data.searchDescription, data.seoTitle, data.title]);

  return (
    <InnerPageChrome currentPath={SITE_ROUTES.servicesArchive}>
      <article className={styles.page}>
        <section className={styles.hero}>
          <div className="container-fluid">
            <div className={styles.heroGrid}>
              <div>
                <span className={styles.eyebrow}>{content.hero_eyebrow}</span>
                <h1 className={styles.heroTitle}>{content.hero_title}</h1>
              </div>

            </div>

            <div className={styles.statRow}>
              {content.stats.map((item) => (
                <article key={`${item.label}-${item.text}`} className={styles.statCard}>
                  <span className={styles.statLabel}>{item.label}</span>
                  <p className={styles.statText}>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.gridSection}>
          <div className="container-fluid">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{content.services_title}</h2>
            </div>

            <div className={styles.serviceGrid}>
              {data.items.map((service, index) => (
                <article
                  key={service.id}
                  className={`${styles.serviceCard} ${
                    index % 2 === 0 ? styles.cardOffsetDown : styles.cardOffsetUp
                  }`}
                >
                  <a className={styles.serviceCardInner} href={service.url}>
                    <div
                      className={styles.serviceImage}
                      style={{ backgroundColor: service.surfaceColor }}
                    >
                      <img
                        src={service.imageUrl}
                        alt={service.imageAlt}
                        style={{ objectPosition: service.imagePosition }}
                      />
                    </div>

                    <div className={styles.serviceBody}>
                      <h3 className={styles.serviceLabel}>{service.title}</h3>
                      <p className={styles.serviceNote}>{service.note}</p>
                      <p className={styles.serviceText}>{service.description}</p>
                      <span
                        className={styles.serviceAccent}
                        style={{ ["--accent" as string]: service.accentColor }}
                      >
                        {service.accentText}
                      </span>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.marqueeSection} aria-hidden="true">
          <div className={styles.marqueeTrack}>
            {[...content.marquee_words, ...content.marquee_words].map((word, index) => (
              <span
                key={`${word}-${index}`}
                className={index % 2 === 0 ? styles.marqueeWordDark : styles.marqueeWordAccent}
              >
                {word}
              </span>
            ))}
          </div>
        </section>

        <section className={styles.supportSection}>
          <div className="container-fluid">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{content.support_title}</h2>
            </div>

            <div className={styles.supportGrid}>
              {content.support_cards.map((card, index) => (
                <article
                  key={card.title}
                  className={`${styles.supportCard} ${
                    index % 3 === 0
                      ? styles.floatRise
                      : index % 3 === 1
                        ? styles.floatFall
                        : styles.floatDrift
                  }`}
                >
                  <span className={styles.supportIndex}>{String(index + 1).padStart(2, "0")}</span>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.ctaSection}>
          <div className="container-fluid">
            <div className={styles.ctaGrid}>
              <div>
                <span className={styles.eyebrow}>{content.cta_eyebrow}</span>
                <h2 className={styles.ctaTitle}>{content.cta_title}</h2>
              </div>

              <div className={styles.ctaActions}>
                <a
                  href={content.primary_link.href}
                  className="btn btn-rounded btn-dark-gray btn-medium btn-switch-text btn-box-shadow text-transform-none"
                >
                  <span>
                    <span className="btn-double-text" data-text={content.primary_link.label}>
                      {content.primary_link.label}
                    </span>
                    <span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </span>
                  </span>
                </a>

                <a href={content.secondary_link.href} className={styles.secondaryLink}>
                  {content.secondary_link.label}
                </a>
              </div>
            </div>
          </div>
        </section>
      </article>
    </InnerPageChrome>
  );
}
